import type { Session } from '@supabase/supabase-js';
import type {
  VolunteerRow,
  VolunteerRole,
  VolunteerStatus,
  CampaignStatsResult,
  ReportKind,
  ReportStatus,
  MaterialCategory,
} from '../types/database';
import type { TabKey, BadgeState, HistoryState, OrgState, SlotState } from '../types/app';

export const VOLUNTEER_STATUS_LABELS: Record<VolunteerStatus, string> = {
  pending: 'Në pritje',
  kontaktuar: 'U kontaktua',
  pa_pergjigje: 'Pa përgjigje',
  ne_autorizim: 'Në pritje të autorizimit',
  approved: 'Aktiv',
  suspended: 'Refuzuar / Pezulluar',
};

export const VOLUNTEER_STATUS_PILLS: Record<VolunteerStatus, { label: string; color: string }> = {
  pending: { label: 'Në pritje', color: 'amber' },
  kontaktuar: { label: 'U kontaktua', color: 'teal' },
  pa_pergjigje: { label: 'Pa përgjigje', color: 'orange' },
  ne_autorizim: { label: 'Në autorizim', color: 'purple' },
  approved: { label: 'Aktiv', color: 'ok' },
  suspended: { label: 'Refuzuar / Pezulluar', color: 'red' },
};

export const ROLES: Record<VolunteerRole, string> = {
  ndihmes: 'Ndihmës',
  mbledhes: 'Mbledhës i autorizuar',
  lw: 'LW',
  koordinator: 'Koordinator',
  jurist: 'Jurist (qendra)',
  logjistike: 'Logjistikë (qendra)',
  burime_njerezore: 'BNj (qendra)',
  pr_edukim: 'PR & Edukim (qendra)',
  it: 'IT (qendra)',
  admin: 'Admin (qendra)',
};

export const QENDRA_ROLES: VolunteerRole[] = ['admin', 'jurist', 'logjistike', 'burime_njerezore', 'pr_edukim', 'it'];
export const STAFF_ROLES: VolunteerRole[] = ['koordinator', 'jurist', 'admin'];

/**
 * Qendra + koordinatorët. Mirrors `vol_is_internal()` in schema.sql, and the
 * INTERNAL_ROLES list in functions/api/send-push.js — these three must agree,
 * or someone gets a notification for something they cannot open in the portal.
 *
 * Distinct from STAFF_ROLES on purpose: STAFF_ROLES is who reviews and approves
 * (koordinator/jurist/admin), while this is who counts as "the centre" for
 * reading internal announcements and being alerted about field reports.
 */
export const INTERNAL_ROLES: VolunteerRole[] = [...QENDRA_ROLES, 'koordinator'];

export const ROLE_DESC: Record<Exclude<VolunteerRole, 'admin'>, string> = {
  ndihmes: 'Ndihmon në terren me mbledhjen e nënshkrimeve dhe detyra të tjera bazë.',
  mbledhes: 'I trajnuar dhe i autorizuar zyrtarisht të mbledhë nënshkrime në terren.',
  lw: 'Mbledhës lëvizës derë-më-derë, pa zonë fikse — mban njësinë e vet me emrin e tij.',
  koordinator: 'Drejton një ose disa zona, organizon turnet dhe njerëzit e terrenit.',
  jurist: 'Ndihmon me pyetje ligjore dhe siguron që mbledhja të jetë brenda ligjit.',
  logjistike: 'Kujdeset për materialet, transportin dhe organizimin praktik të fushatës.',
  burime_njerezore: 'Organizon ekipet e vullnetarëve, rekrutimin dhe mirëqenien e tyre.',
  pr_edukim: 'Kujdeset për komunikimin publik dhe informimin e qytetarëve.',
  it: 'Mirëmban portalin, të dhënat dhe sistemet digjitale të fushatës.',
};

/**
 * Roles a volunteer may request when signing up. `it` and `lw` are deliberately
 * left out — the centre assigns those roles itself, so they are not offered at
 * signup. Existing IT/LW volunteers and admin role assignment are unaffected.
 */
export const SIGNUP_ROLES: Array<Exclude<VolunteerRole, 'admin'>> =
  (Object.keys(ROLE_DESC) as Array<Exclude<VolunteerRole, 'admin'>>).filter(r => r !== 'it' && r !== 'lw');

export const KINDS: Record<ReportKind, { ic: string; lb: string; d: string }> = {
  incident: { ic: '🚨', lb: 'Incident', d: 'Pengesë, presion, konflikt në terren' },
  legal: { ic: '⚖️', lb: 'Shqetësim ligjor', d: 'Pyetje ose problem me procedurën/ligjin' },
  material: { ic: '📦', lb: 'Material i humbur', d: 'Formularë, ID, fletushka të humbura/dëmtuara' },
};

export const REPORT_STATUS: Record<ReportStatus, [string, string]> = {
  open: ['Hapur', 'red'],
  review: ['Në shqyrtim', 'amber'],
  resolved: ['E zgjidhur', 'ok'],
};

export const CATS: Record<MaterialCategory, [string, string]> = {
  guide: ['📘', 'Guide-book / manuale'],
  leaflet: ['📄', 'Fletë-palosje'],
  form: ['🖊️', 'Formularë'],
  faq: ['❓', 'Pyetjet e shpeshta'],
  legal: ['⚖️', 'Dokumente ligjore'],
  other: ['📎', 'Të tjera'],
};

export class AppState {
  public ME: VolunteerRow | null = null;
  public SESSION: Session | null = null;
  public STATS: Partial<CampaignStatsResult> = {};
  public activeTab: TabKey = 'home';

  public BADGE: BadgeState = {
    priv: null,
    units: [],
    reqs: {},
  };

  public HIST: HistoryState = {
    rows: [],
    units: [],
    unit: '',
    from: '',
    to: '',
    page: 1,
    limit: 100,
    totalRows: 0,
    summary: null,
  };

  public ORG: OrgState = {
    groups: [],
    kidsOf: {},
    sel: null,
  };

  public SLOTS: Record<string, SlotState> = {};
  public SLOT_POP: string | null = null;

  public isStaff(): boolean {
    return !!this.ME && STAFF_ROLES.includes(this.ME.role);
  }

  public isAdmin(): boolean {
    return !!this.ME && this.ME.role === 'admin';
  }

  public isFeedbackAdmin(): boolean {
    return !!this.ME && (this.ME.role === 'admin' || this.ME.role === 'it');
  }

  public isField(): boolean {
    return !!this.ME && ['ndihmes', 'mbledhes', 'lw'].includes(this.ME.role);
  }

  public isQendra(): boolean {
    return !!this.ME && QENDRA_ROLES.includes(this.ME.role);
  }

  /** Qendra + koordinatorët — writes announcements, gets alerted about reports. */
  public isInternal(): boolean {
    return !!this.ME && INTERNAL_ROLES.includes(this.ME.role);
  }

  public isTeamLead(): boolean {
    return !!this.ME && ['koordinator', 'mbledhes', 'lw'].includes(this.ME.role);
  }

  public isTeamRole(): boolean {
    return !!this.ME && ['ndihmes', 'mbledhes', 'koordinator', 'lw'].includes(this.ME.role);
  }
}

export const store = new AppState();
